import { useNavigate } from 'react-router-dom';
import { useProducts, useEcosystemStats } from '@/hooks/use-products';
import { MetricCard } from '@/components/ui/metric-card';
import { HealthRing } from '@/components/ui/health-ring';
import { MaturityBadge } from '@/components/ui/maturity-badge';
import { healthColor, healthTextClass, formatNumber, relativeTime } from '@/lib/format';
import type { Product } from '@/lib/types';

function Sparkline({ data, score }: { data: number[]; score: number | null }) {
  if (!data || data.length < 2) {
    return <div className="h-full flex items-center justify-center text-[0.6rem] text-text-muted">No trend data</div>;
  }

  const min = Math.min(...data) - 5;
  const max = Math.max(...data) + 5;
  const range = max - min || 1;
  const w = 200;
  const h = 32;
  const step = w / (data.length - 1);

  const points = data.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(' ');
  const fillPoints = `${points} ${w},${h} 0,${h}`;
  const strokeColor = healthColor(score);
  const gradId = `sparkGrad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.12" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline fill="none" stroke={strokeColor} strokeWidth="1.5" points={points} />
      <polygon fill={`url(#${gradId})`} points={fillPoints} />
    </svg>
  );
}

function ProductCard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const testCount = product.latest_test_count;
  const deployCount = product.deploy_count_30d;
  const vulnCount = product.vuln_count;

  return (
    <div
      className="bg-bg-card border border-border rounded-lg p-5 cursor-pointer transition-all duration-200 hover:border-[#2A2A2A] hover:bg-[#1A1A1A] hover:-translate-y-px group"
      onClick={() => navigate(`/products/${product.slug}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/products/${product.slug}`); }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[0.95rem] font-semibold font-display">{product.name}</h3>
          <div className="text-[0.72rem] text-text-muted">{product.description || 'No description'}</div>
        </div>
        <HealthRing score={product.health_score} size={56} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center">
          <div className="font-mono text-[0.82rem] font-semibold">
            {testCount != null ? formatNumber(testCount) : '—'}
          </div>
          <div className="text-[0.6rem] text-text-muted uppercase tracking-wider">Tests</div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[0.82rem] font-semibold">
            {deployCount != null ? deployCount : '—'}
          </div>
          <div className="text-[0.6rem] text-text-muted uppercase tracking-wider">Deploys</div>
        </div>
        <div className="text-center">
          <div className={`font-mono text-[0.82rem] font-semibold ${vulnCount != null && vulnCount === 0 ? 'text-success' : vulnCount != null && vulnCount > 0 ? 'text-danger' : ''}`}>
            {vulnCount != null ? vulnCount : '—'}
          </div>
          <div className="text-[0.6rem] text-text-muted uppercase tracking-wider">Vulns</div>
        </div>
      </div>

      <div className="h-8 mb-3">
        <Sparkline data={product.health_trend} score={product.health_score} />
      </div>

      <div className="flex items-center justify-between">
        <MaturityBadge level={product.maturity_level} />
        <span className="text-[0.68rem] text-text-muted">
          {product.last_deploy_at ? relativeTime(product.last_deploy_at) : 'No deploys'}
        </span>
      </div>
    </div>
  );
}

export function EcosystemPage() {
  const { data: products, isLoading } = useProducts();
  const { data: stats } = useEcosystemStats();

  const reporting = products?.filter(p => p.health_score !== null && p.health_score > 0) ?? [];
  const avgHealth = reporting.length > 0
    ? Math.round(reporting.reduce((sum, p) => sum + (p.health_score ?? 0), 0) / reporting.length)
    : 0;
  const goldCount = products?.filter(p => p.maturity_level === 'gold').length ?? 0;
  const totalProducts = products?.length ?? 0;

  const totalVulns = (stats?.open_vulns_critical ?? 0) + (stats?.open_vulns_high ?? 0);

  if (isLoading) {
    return (
      <div>
        <div className="mb-8">
          <div className="h-7 w-48 bg-bg-elevated rounded animate-pulse mb-1" />
          <div className="h-4 w-72 bg-bg-elevated rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-8">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="bg-bg-card border border-border rounded-md p-5 h-24 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="bg-bg-card border border-border rounded-lg p-5 h-52 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold tracking-tight">Ecosystem Overview</h1>
        <p className="text-text-secondary text-[0.82rem] mt-1">Health status across all Vyralab products</p>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-8">
        <MetricCard
          label="Avg Health Score"
          value={avgHealth}
          valueClassName={healthTextClass(avgHealth > 0 ? avgHealth : null)}
          sub={`${reporting.length} of ${totalProducts} products reporting`}
        />
        <MetricCard
          label="Total Tests"
          value={stats ? formatNumber(stats.total_tests) : '—'}
          sub="Across all products"
        />
        <MetricCard
          label="Deploys This Week"
          value={stats ? stats.deploys_this_week : '—'}
          sub={stats?.deploy_breakdown || 'All components'}
        />
        <MetricCard
          label="Open Vulnerabilities"
          value={stats ? totalVulns : '—'}
          valueClassName={stats && totalVulns === 0 ? 'text-success' : totalVulns > 0 ? 'text-danger' : ''}
          sub={stats ? `${stats.open_vulns_critical} critical, ${stats.open_vulns_high} high (prod)` : 'Critical + High (prod)'}
        />
        <MetricCard
          label="Products at Gold"
          value={<>{goldCount}<span className="text-sm text-text-muted font-normal"> / {totalProducts}</span></>}
          sub={products?.filter(p => p.maturity_level === 'gold').map(p => p.name).join(', ') || '—'}
        />
      </div>

      {products && products.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
          {products.map(p => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-text-muted">
          <div className="text-2xl mb-3 opacity-40">&#9632;</div>
          <h3 className="text-base text-text-secondary mb-1">No products registered</h3>
          <p className="text-[0.78rem] max-w-[400px] mx-auto">
            Register your first product in Settings to start tracking quality data.
          </p>
        </div>
      )}
    </div>
  );
}
